/**
 * @cfleet/api-client - Organization Hook
 *
 * React Query hook for fetching the current user's organization details.
 * Calls GET /organizations/me which returns {id, name, slug, trial_ends_at,
 * plan, subscription_status, has_card_on_file}.
 *
 * This serves as a fallback for getting org details when they weren't
 * included in the login response (e.g., session hydration from cookies).
 *
 * @example
 * ```tsx
 * import { useMyOrganization } from '@cfleet/api-client';
 *
 * const { data: org } = useMyOrganization({ authConfig });
 * // org?.name -> "Acme Fleet Co"
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { createAuthenticatedCoreClient, type AuthMiddlewareConfig } from '../index';

/**
 * Subscription tier, per the tiered flat-monthly pricing model.
 */
export type BillingPlan = 'starter' | 'growth' | 'pro' | 'enterprise';

/**
 * Current billing state. Operational writes are blocked (read-only) once
 * the trial has ended and this is not "active".
 */
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled';

/**
 * Organization detail from GET /organizations/me
 */
export interface OrganizationDetail {
  id: string;
  name: string;
  slug?: string;
  /** ISO 8601 timestamp. Null/absent for organizations not on a trial. */
  trial_ends_at?: string;
  plan?: BillingPlan;
  subscription_status: SubscriptionStatus;
  has_card_on_file: boolean;
}

/**
 * True once an organization's trial has lapsed without an active
 * subscription — operational writes are blocked, but login and
 * Settings -> Billing stay reachable. Mirrors the server-side
 * `OrganizationRecord::is_write_blocked` check exactly: `trialing` is the
 * only status gated on `trial_ends_at` (it's the signup-time default and can
 * legitimately have no end date yet); `past_due` and `canceled` block
 * unconditionally, since a grandfathered organization with no trial date
 * that later goes past_due/canceled via Stripe must not keep write access.
 */
export function isOrganizationReadOnly(org: Pick<OrganizationDetail, 'subscription_status' | 'trial_ends_at'>): boolean {
  if (org.subscription_status === 'active') return false;
  if (org.subscription_status !== 'trialing') return true;
  if (!org.trial_ends_at) return false;
  return new Date(org.trial_ends_at).getTime() < Date.now();
}

/**
 * Configuration for organization hooks
 */
export interface UseOrganizationHooksConfig {
  authConfig: AuthMiddlewareConfig;
}

/**
 * Hook to fetch the current user's organization details.
 *
 * Uses GET /organizations/me endpoint. Requires authentication.
 * The backend resolves the organization from the JWT tenant context.
 *
 * @param config - Configuration including auth config
 * @returns Query hook with organization details
 */
export function useMyOrganization(config?: UseOrganizationHooksConfig) {
  return useQuery({
    queryKey: ['organization', 'me'],
    queryFn: async (): Promise<OrganizationDetail> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createAuthenticatedCoreClient(config.authConfig);
      const response = await client.GET('/organizations/me' as never);
      const { data, error } = response as { data?: OrganizationDetail; error?: { detail?: string } };

      if (error) {
        throw new Error(error.detail || 'Failed to fetch organization');
      }

      return data!;
    },
    enabled: !!config?.authConfig,
  });
}
